import {
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	input,
	OnInit,
	output,
	signal,
	untracked,
	WritableSignal,
	ChangeDetectionStrategy,
} from '@angular/core';
import { fadeInUp400ms } from '../../../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../../../@vex/animations/stagger.animation';
import { BreadCrumbService } from '../../../../../../@vex/components/breadcrumbs/BreadCrumbService';
import {
	BillingPeriod,
	CostAllocation,
	CostGroup,
	CostGroupCategory,
	CostGroupRequest,
	CostGroupService,
	CostGroupType,
	Exemption,
	ExemptionService,
	Flatrate,
	FlatrateService,
	Invoice,
	InvoiceAmountType,
	InvoiceAmountValue,
	InvoiceCategory,
	InvoiceCategoryType,
	InvoiceDeliveryValue,
	InvoiceService,
	VatType,
} from '@eadsysteme/billingclient-v1';
import { DateUtil } from '../../../../../util/DateUtil';
import { SessionService } from '../../../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, of, zip } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { MatCell, MatCellDef, MatColumnDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { Icons } from 'src/app/util/Icons';
import { MatIcon } from '@angular/material/icon';
import { MatSort } from '@angular/material/sort';
import { AsyncPipe, DatePipe, DecimalPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { translate, TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { CostGroupDialogComponent } from '../add-cost-dialog/cost-group-dialog.component';
import { InvoiceDialogComponent, InvoiceDialogData } from './invoice/invoice-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatTooltip } from '@angular/material/tooltip';
import { ConfirmationDialogComponent } from '../../../../confirmation-dialog/confirmation-dialog.component';
import { AssignAllocationDialogComponent } from '../assign-allocation-dialog/assign-allocation-dialog.component';
import { CostAllocationHelper } from '../../../../../services/CostAllocationHelper.service';
import { CostAllocationDetail } from '../../../../../services/data/CostAllocationDetail';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatBadge } from '@angular/material/badge';
import { NumberUtil } from '../../../../../util/NumberUtil';
import { FlatratesExemptionsComponent } from '../flatrates-exemptions/flatrates-exemptions.component';
import { AllocationDialogComponent } from '../../basic-data/allocations/allocation-dialog/allocation-dialog.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AllocationOptionComponent } from '../shared/allocation-option/allocation-option.component';
import { AraPopoverComponent } from '../../../../util/ara-popover/popover.component';
import { FlatrateExemptionService } from '../flatrates-exemptions/flatrates-exemption.service';

type GroupedFlatRow = { category: InvoiceCategory; invoices: MatTableDataSource<Invoice> };

interface HistoricalComparison {
	previousAmountValues: InvoiceAmountValue[];
	currentAmountValues: InvoiceAmountValue[];
	previousTotal: number;
	currentTotal: number;
	deviation: number;
	deviationPercent: number;
	isInfinite?: boolean;
	color: 'gray' | 'orange' | 'red';
	arrowIcon: string;
}

@Component({
	selector: 'vex-cost',
	templateUrl: './cost.component.html',
	styleUrl: './cost.component.scss',
	host: {},
	imports: [
		ReactiveFormsModule,
		MatIcon,
		MatSort,
		MatColumnDef,
		MatCellDef,
		MatCell,
		MatRowDef,
		AsyncPipe,
		MatTable,
		MatRow,
		TranslocoDirective,
		NgTemplateOutlet,
		DatePipe,
		MatButton,
		MatMenu,
		MatMenuItem,
		MatIconButton,
		MatMenuTrigger,
		MatExpansionPanel,
		MatExpansionPanelHeader,
		MatExpansionPanelTitle,
		MatTooltip,
		MatChipsModule,
		NgClass,
		MatFormField,
		MatLabel,
		MatOption,
		MatSelect,
		MatSelectTrigger,
		FormsModule,
		MatBadge,
		FlatratesExemptionsComponent,
		MatProgressSpinner,
		AllocationOptionComponent,
		AraPopoverComponent,
	],
	providers: [DecimalPipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class CostComponent implements OnInit {
	protected readonly breadCrumbService = inject(BreadCrumbService);
	protected readonly numberUtil = inject(NumberUtil);
	protected readonly flatratesExemptionsService = inject(FlatrateExemptionService);
	protected readonly dateUtil = inject(DateUtil);
	protected readonly icons = inject(Icons);
	private readonly flatrateService = inject(FlatrateService);
	private readonly exemptionService = inject(ExemptionService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly costAllocationDetailService = inject(CostAllocationHelper);
	private readonly invoiceService = inject(InvoiceService);
	private readonly dialog = inject(MatDialog);
	private readonly sessionService = inject(SessionService);
	private readonly snackBar = inject(SnackbarService);
	private readonly costGroupService = inject(CostGroupService);
	private readonly formBuilder = inject(FormBuilder);
	private readonly transLocoService = inject(TranslocoService);

	readonly billingId = input<string>(undefined);
	readonly allCostsExpanded$ = input<Observable<boolean>>(undefined);
	readonly period = input<BillingPeriod>(undefined);
	readonly costGroupTypes = input<WritableSignal<CostGroupType[]>>(undefined);
	readonly costGroupType = input<CostGroupType>(undefined);
	readonly previousCostGroups = input<CostGroup[]>([]);
	readonly previousPeriod = input<BillingPeriod>(undefined);

	/** View mode for invoices: 'table' or 'card' */
	viewMode = input<'table' | 'card'>('table');

	// input costGroup from the parent component
	inputCostGroup = input<CostGroup>();

	// Emits the updated CostGroup (reloaded from backend after edits/calculations) or null when this CostGroup was deleted,
	// so the parent can update/remove it in its CostGroup[] list.
	inputCostGroupChange = output<CostGroup | null>();

	protected readonly isLoading = signal<boolean>(false);
	/** Temporary local override when we just refreshed from backend. */
	private readonly displayCostGroup = signal<CostGroup | undefined>(undefined);
	/** Single source of truth for rendering/logic. */
	protected readonly costGroup = computed(() => this.displayCostGroup() ?? this.inputCostGroup());

	tabIndexPreserve = output<string>();
	navigateToAllocations = output<void>();
	protected isExpanded = false;

	protected costFormGroup: FormGroup;
	protected readonly CostGroupCategory = CostGroupCategory;
	protected currentAllocation = signal<CostAllocationDetail>(undefined);
	protected showFlatratesExemptionsOfCostGroup: WritableSignal<CostGroup> = signal(undefined);
	protected allocationExists: WritableSignal<boolean> = signal(false);
	protected isEditable = signal<boolean>(true);
	protected selectedNewAllocation: WritableSignal<CostAllocationDetail> = signal(undefined);
	protected originalAllocationId: WritableSignal<string | undefined> = signal(undefined);
	protected editableAllocationCostGroup: WritableSignal<CostGroup> = signal(undefined);
	protected availableAllocations: WritableSignal<CostAllocationDetail[]> = signal([]);
	protected hasAllocationChanged = computed(() => {
		const selected = this.selectedNewAllocation();
		const original = this.originalAllocationId();
		return selected?.exportId !== original;
	});
	protected allocationAutoSelected = signal(false);

	private readonly previousPeriodInvoicesByCategoryType = new Map<InvoiceCategoryType, Invoice[]>();
	protected invoiceCategoryInvoicesMap: Map<string, MatTableDataSource<Invoice>> = new Map();
	protected invoiceCategoryInvoiceDataSource: GroupedFlatRow[] = [];
	protected displayedCategoryColumns = ['summary', 'actions'];

	exemptionCount: WritableSignal<number> = signal(0);
	flatrateCount: WritableSignal<number> = signal(0);

	constructor() {
		this.costFormGroup = this.formBuilder.group({
			costType: ['', Validators.required],
			description: ['', Validators.required],
			name: ['', Validators.required],
			printname: ['', Validators.required],
		});

		effect(
			() => {
				const cg = this.costGroup();
				if (cg) {
					untracked(() => {
						this.fillCostGroup(cg);
						this.loadInvoicesByCostGroup();
					});
				}
			},
			{ allowSignalWrites: true }
		);

		effect(
			() => {
				this.inputCostGroup();
				untracked(() => this.displayCostGroup.set(undefined));
			},
			{ allowSignalWrites: true }
		);
	}

	ngOnInit(): void {
		this.costFormGroup.disable();
		this.isEditable.set(false);

		if (this.previousCostGroups()?.length > 0) {
			this.loadPreviousPeriodInvoices();
		}

		zip(
			this.flatrateService.readAllFlatrate(this.sessionService.effectiveArea, this.billingId(), this.costGroup().exportId),
			this.exemptionService.readAllExemption(this.sessionService.effectiveArea, this.billingId(), this.costGroup().exportId)
		)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ([flatrates, exemptions]: [Exemption[], Flatrate[]]) => {
					this.exemptionCount.set(exemptions.length);
					this.flatrateCount.set(flatrates.length);
				},
			});

		this.allCostsExpanded$()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((allCostsExpanded) => {
				if (allCostsExpanded !== undefined) {
					this.isExpanded = allCostsExpanded;
				}
			});

		this.flatratesExemptionsService.showDetail$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
			if (!value) {
				const cg = untracked(() => this.costGroup());
				if (cg?.exportId) {
					this.emitCostGroupChange(cg.exportId);
				}
				this.showFlatratesExemptionsOfCostGroup.set(undefined);
			}
		});
	}

	/**
	 * Emits a cost group change and (optionally) reloads it from backend to get calculated fields.
	 * Use this instead of calling `inputCostGroupChange.emit(...)` directly.
	 */
	private emitCostGroupChange(costGroupExportId: string): void {
		const cg = this.costGroup();
		if (!cg?.exportId) {
			return;
		}

		this.isLoading.set(true);
		this.costGroupService
			.readCostGroup(this.sessionService.effectiveArea, this.billingId(), costGroupExportId)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				catchError(() => {
					this.isLoading.set(false);
					return of(undefined);
				})
			)
			.subscribe((fresh) => {
				if (!fresh?.exportId) {
					this.isLoading.set(false);
					return;
				}
				this.displayCostGroup.set(fresh);
				this.inputCostGroupChange.emit({ ...fresh });
			});
	}

	private fillCostGroup(costGroup: CostGroup) {
		this.costFormGroup.get('name').setValue(costGroup.name);
		const costType = this.costGroupTypes()().find((value) => value.exportId === costGroup?.costGroupTypeExportId);
		this.costFormGroup.get('costType').setValue(costType);

		if (costGroup.costAllocationExportId) {
			this.costAllocationDetailService.loadAllocationDetails(this.billingId(), this.period().exportId).then((details) => {
				const existingAllocation = details.find((a) => a.exportId === costGroup.costAllocationExportId);
				if (existingAllocation?.exportId) {
					this.allocationExists.set(true);
					this.currentAllocation.set(existingAllocation);
				}
			});
		} else {
			this.allocationExists.set(false);
			this.currentAllocation.set(undefined);
		}
	}

	editCostGroup(costGroup: CostGroup) {
		this.tabIndexPreserve.emit(this.costGroupType().exportId);

		const dialogRef = this.dialog.open(CostGroupDialogComponent, {
			disableClose: false,
			width: '670px',
			data: {
				billingId: this.billingId(),
				billingPeriodId: this.period().exportId,
				currentCostGroup: costGroup,
				currentCostGroupType: this.costFormGroup.get('costType').value,
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result?.saved && result.data?.exportId) {
				this.emitCostGroupChange(costGroup.exportId);
			}
		});
	}

	loadPreviousPeriodInvoices() {
		const previousCostGroups = this.previousCostGroups();
		if (!previousCostGroups || previousCostGroups.length === 0) {
			return;
		}

		const previousCostGroupId = this.costGroup()?.previousCostGroupExportId;
		if (!previousCostGroupId) {
			return;
		}

		const previousCostGroup = previousCostGroups.find((cg) => cg.exportId === previousCostGroupId);
		if (!previousCostGroup?.invoiceCategories) {
			return;
		}

		this.previousPeriodInvoicesByCategoryType.clear();

		for (const category of previousCostGroup.invoiceCategories) {
			if (!category.exportId || !category.type) {
				continue;
			}

			this.invoiceService
				.readAllInvoice(this.sessionService.effectiveArea, this.billingId(), category.exportId)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (invoices) => {
						const existing = this.previousPeriodInvoicesByCategoryType.get(category.type) || [];
						this.previousPeriodInvoicesByCategoryType.set(category.type, [...existing, ...(invoices || [])]);
					},
					error: () => {
						// Silent fail - Vergleichsdaten sind optional
					},
				});
		}
	}

	deleteCostGroup(costGroup: CostGroup) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.transLocoService.translate('errors.warning');
		dialogRef.componentInstance.confirmMessage = this.transLocoService.translate('billing.confirmDeleteCostGroup', {
			name: costGroup.name,
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.isEditable.set(false);
				this.costFormGroup.disable();
				this.costGroupService
					.deleteCostGroup(this.sessionService.effectiveArea, this.billingId(), costGroup.exportId)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: () => {
							this.inputCostGroupChange.emit(null);
							this.snackBar.success(this.transLocoService.translate('billing.deleteCostGroupSuccess'));
						},
						error: () => {
							this.snackBar.error(this.transLocoService.translate('billing.deleteCostGroupFailed'));
						},
					});
			}
		});
	}

	getComparisonColorClass(color: 'gray' | 'orange' | 'red'): string {
		switch (color) {
			case 'red':
				return 'text-red-500';
			case 'orange':
				return 'text-orange-500';
			case 'gray':
				return 'text-gray-500';
		}
	}

	toInvoice(row: Invoice, category: InvoiceCategory) {
		if (this.period().locked) {
			return;
		}
		this.openInvoiceDialog(row, category);
	}

	/**
	 * Unterscheidet Rechnungen anhand des Discriminators `dtype` ('Customer' | 'ServiceCompany').
	 * Im customerPortal (Hausverwaltungs-Sicht) sind nur Customer-Rechnungen (HV) editierbar;
	 * ServiceCompany-Rechnungen (MD) werden read-only/inaktiv dargestellt.
	 */
	protected isCustomerInvoice(invoice: Invoice): boolean {
		return invoice.dtype === 'Customer';
	}

	deleteInvoice(row: Invoice) {
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.transLocoService.translate('errors.warning');
		dialogRef.componentInstance.confirmMessage = this.transLocoService.translate('billing.confirmDeleteInvoice', {
			name: row.name,
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.invoiceService
					.deleteInvoice(this.sessionService.effectiveArea, this.billingId(), row.exportId)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: () => {
							this.snackBar.success(this.transLocoService.translate('billing.deleteInvoiceSuccess'));
							this.emitCostGroupChange(this.costGroup().exportId);
						},
						error: () => {
							this.snackBar.error(this.transLocoService.translate('errors.deleteInvoice'));
						},
					});
			}
		});
	}

	protected openAssignAllocationDialog(costGroup: CostGroup) {
		const matDialogRef = this.dialog.open(AssignAllocationDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
			data: {
				costGroup: costGroup,
				billingId: this.billingId(),
				billingPeriod: this.period(),
				costGroupType: this.costGroupType(),
			},
		});
		matDialogRef.afterClosed().subscribe({
			next: (result) => {
				if (result) {
					this.emitCostGroupChange(costGroup.exportId);
				}
			},
		});
	}

	getInvoiceCount(costGroup: CostGroup): number {
		if (!costGroup?.invoiceCategories?.length) {
			return 0;
		}

		return costGroup.invoiceCategories.reduce((sum, c) => {
			const invoices = this.invoiceCategoryInvoicesMap.get(c.exportId)?.data ?? [];
			return sum + invoices.length;
		}, 0);
	}

	openExemptions(currentCostGroup: CostGroup) {
		this.showFlatratesExemptionsOfCostGroup.set(currentCostGroup);
		this.flatratesExemptionsService.activateView('EXEMPTION');
	}

	openFlatrates(currentCostGroup: CostGroup) {
		this.showFlatratesExemptionsOfCostGroup.set(currentCostGroup);
		this.flatratesExemptionsService.activateView('FLATRATE');
	}

	getInvoiceCountForCategory(category: InvoiceCategory): number {
		return this.invoiceCategoryInvoicesMap.get(category.exportId)?.data?.length ?? 0;
	}

	isInvoiceCategoryDisabled(category: InvoiceCategory): boolean {
		// Kategorien, die nur einmal anlegbar sein sollen
		const singleAllowedTypes: InvoiceCategoryType[] = [
			// 'INITIAL_STOCK',
			//   'REMAINING_STOCK',
			InvoiceCategoryType.FINAL_REMAINING_STOCK,
		];

		if (!singleAllowedTypes.includes(category.type)) {
			return false; // frei anlegbar
		}

		return this.getInvoiceCountForCategory(category) > 0;
	}

	getTranslatedCommodity(deliveryValue: InvoiceDeliveryValue): string | undefined {
		const delivery = this.costGroup()?.invoiceDeliveries?.find((d) => d.exportId === deliveryValue.invoiceDeliveryExportId);
		return delivery ? translate('commodity.' + delivery.commodity) : undefined;
	}

	isInvoiceCategoryRemainingStock(row: Invoice) {
		this.costGroup()?.invoiceCategories.forEach((c) => {
			const newVar = this.invoiceCategoryInvoicesMap.get(c.exportId);
			if (newVar.data.find((invoice) => invoice.exportId === row.exportId)) {
				if (c.type === InvoiceCategoryType.REMAINING_STOCK) {
					return true;
				}
			}
		});
		return false;
	}

	getInvoiceAmountValues(currentCostGroup: CostGroup, invoice: Invoice): string[] {
		return invoice.invoiceAmountValues
			.filter((amountValue): amountValue is NonNullable<typeof amountValue> => amountValue != null)
			.map((amountValue) => {
				const invoiceAmountType = this.getInvoiceAmountType(currentCostGroup, amountValue);
				const typeLabel = this.t('billing.invoiceAmountTypeChips.' + invoiceAmountType);
				const invoiceAmountDisplayLine = this.getInvoicesAmountDisplayWithVatExpectation(
					amountValue.grossAmount,
					amountValue.netAmount,
					amountValue.vatAmount
				);
				return `${typeLabel}: ${invoiceAmountDisplayLine}`;
			});
	}

	/**
	 * Retrieves the type of invoice amount by matching the `invoiceAmountId` of the provided `invoiceAmountValue`
	 * with the `invoiceAmounts` in the given `costGroup`.
	 *
	 * @param {CostGroup} costGroup - The cost group containing a collection of invoice amounts to search through.
	 * @param {InvoiceAmountValue} invoiceAmountValue - The value object containing the ID of the invoice amount to locate.
	 * @return {InvoiceAmountType | undefined} The type of the matched invoice amount, or `undefined` if no match is found.
	 */
	private getInvoiceAmountType(costGroup: CostGroup, invoiceAmountValue: InvoiceAmountValue): InvoiceAmountType | undefined {
		for (const invoiceAmount of costGroup.invoiceAmounts) {
			if (invoiceAmount.exportId === invoiceAmountValue.invoiceAmountExportId) {
				return invoiceAmount.type;
			}
		}

		return undefined;
	}

	private getInvoicesAmountDisplayWithVatExpectation(
		grossAmount: number | null | undefined,
		netAmount: number | null | undefined,
		vatAmount: number | null | undefined
	): string {
		const vatType = this.period()?.vatType;
		const translations = {
			grossMissing: this.t('billing.amount_missing.gross'),
			netMissing: this.t('billing.amount_missing.net'),
			vatMissing: this.t('billing.amount_missing.vat'),
			vatShort: this.t('billing.vat_short'),
			unknown: this.t('common.unknown'),
		};

		const grossPart = grossAmount == null ? translations.grossMissing : this.toCurrency(grossAmount);
		const netPart = netAmount == null ? translations.netMissing : this.toCurrency(netAmount);
		const vatPart = vatAmount == null ? translations.vatMissing : `${this.toCurrency(vatAmount)} ${translations.vatShort}`;

		switch (vatType) {
			case VatType.GROSS_VAT:
				return `${grossPart} (${vatPart})`;
			case VatType.NET_VAT:
				return `${netPart} + ${vatPart}`;
			case VatType.GROSS:
				return grossPart;
			case VatType.NET:
				return netPart;
			case VatType.GROSS_NET_VAT:
				return `${grossPart} (${netPart} + ${vatPart})`;
			default:
				return translations.unknown;
		}
	}

	private rebuildFlatTable(): void {
		const categoryRows: GroupedFlatRow[] = [];
		const categories = this.costGroup()?.invoiceCategories ?? [];

		for (const currentCategory of categories) {
			const invoices = this.invoiceCategoryInvoicesMap.get(currentCategory.exportId)?.data ?? [];
			categoryRows.push({ category: currentCategory, invoices: new MatTableDataSource(invoices) });
		}

		this.invoiceCategoryInvoiceDataSource = categoryRows;
		this.isLoading.set(false);
	}

	private getRelevantAmount(invoiceAmountValues: InvoiceAmountValue[]): number {
		const vatType = this.period()?.vatType;

		return invoiceAmountValues.reduce((sum, amountValue) => {
			let amount: number;

			switch (vatType) {
				case VatType.GROSS_VAT:
				case VatType.GROSS:
				case VatType.GROSS_NET_VAT:
					amount = amountValue.grossAmount || 0;
					break;
				case VatType.NET_VAT:
				case VatType.NET:
					amount = amountValue.netAmount || 0;
					break;
				default:
					// Fallback: Gross
					amount = amountValue.grossAmount || 0;
			}

			return sum + amount;
		}, 0);
	}

	private collectPreviousInvoiceAmountValues(categoryType: InvoiceCategoryType): InvoiceAmountValue[] {
		const allAmountValues: InvoiceAmountValue[] = [];

		const invoices = this.previousPeriodInvoicesByCategoryType.get(categoryType) || [];
		for (const invoice of invoices) {
			if (invoice.invoiceAmountValues) {
				allAmountValues.push(...invoice.invoiceAmountValues);
			}
		}

		return allAmountValues;
	}

	private collectCurrentInvoiceAmountValues(categoryType: InvoiceCategoryType): InvoiceAmountValue[] {
		const allAmountValues: InvoiceAmountValue[] = [];

		const category = this.costGroup()?.invoiceCategories?.find((cat) => cat.type === categoryType);

		if (category) {
			const ds = this.invoiceCategoryInvoicesMap.get(category.exportId);
			const data = ds?.data;
			if (data) {
				for (const invoice of data) {
					if (invoice.invoiceAmountValues) {
						allAmountValues.push(...invoice.invoiceAmountValues);
					}
				}
			}
		}

		return allAmountValues;
	}

	getHistoricalComparison(category: InvoiceCategory): HistoricalComparison | null {
		// Prüfe ob Category-Type erlaubt ist
		const allowedTypes: InvoiceCategoryType[] = [InvoiceCategoryType.BILL, InvoiceCategoryType.CONSUMPTION, InvoiceCategoryType.FEED_IN];
		if (!category?.type || !allowedTypes.includes(category.type)) {
			return null;
		}

		// Prüfungen: previousPeriod vorhanden
		const previousCostGroups = this.previousCostGroups();
		if (!previousCostGroups || previousCostGroups.length === 0) {
			return null;
		}

		const previousAmountValues = this.collectPreviousInvoiceAmountValues(category.type);
		const currentAmountValues = this.collectCurrentInvoiceAmountValues(category.type);

		// Berechne Totals basierend auf VAT-Type
		const previousTotal = this.getRelevantAmount(previousAmountValues);
		const currentTotal = this.getRelevantAmount(currentAmountValues);

		// Keine Anzeige, wenn BEIDE keine Daten haben
		if (previousTotal === 0 && currentTotal === 0) {
			return null;
		}

		const deviation = previousTotal - currentTotal;

		const { deviationPercent, isInfinite } = this.calculateDeviation(currentTotal, previousTotal, deviation);

		const { color, arrowIcon } = this.getColorArrowByDeviation(isInfinite, deviationPercent);

		return {
			previousAmountValues,
			currentAmountValues,
			previousTotal,
			currentTotal,
			deviation,
			deviationPercent,
			isInfinite,
			color,
			arrowIcon,
		};
	}

	private calculateDeviation(currentTotal: number, previousTotal: number, deviation: number) {
		let deviationPercent: number;
		let isInfinite = false;

		if (currentTotal === 0 && previousTotal !== 0) {
			isInfinite = true;
			deviationPercent = previousTotal > 0 ? Infinity : -Infinity;
		} else if (currentTotal !== 0 && previousTotal === 0) {
			deviationPercent = -100;
		} else {
			deviationPercent = (deviation / currentTotal) * 100;
		}
		return { deviationPercent, isInfinite };
	}

	private getColorArrowByDeviation(isInfinite: boolean, deviationPercent: number): { color: 'gray' | 'orange' | 'red'; arrowIcon: string } {
		const absDeviationPercent = Math.abs(deviationPercent);
		const color = this.getDeviationColor(isInfinite, absDeviationPercent);
		const arrowIcon = this.getDeviationArrowIcon(deviationPercent, absDeviationPercent);
		return { color, arrowIcon };
	}

	private getDeviationColor(isInfinite: boolean, absDeviationPercent: number): 'gray' | 'orange' | 'red' {
		if (isInfinite || absDeviationPercent > 70) {
			return 'red';
		}
		if (absDeviationPercent <= 30) {
			return 'gray';
		}
		return 'orange';
	}

	private getDeviationArrowIcon(deviationPercent: number, absDeviationPercent: number): string {
		if (absDeviationPercent <= 30 && deviationPercent === 0) {
			return 'remove';
		}
		return deviationPercent > 0 ? 'arrow_upward' : 'arrow_downward';
	}

	formatComparisonAmount(amountValues: InvoiceAmountValue[]): string {
		// Nutze die existierende Formatierungslogik mit VAT-Anzeige
		return amountValues
			.map((amountValue) => this.getInvoicesAmountDisplayWithVatExpectation(amountValue?.grossAmount, amountValue?.netAmount, amountValue?.vatAmount))
			.join(' | ');
	}

	formatPreviousPeriodDates(): string {
		const previousPeriod = this.previousPeriod();
		if (!previousPeriod?.validFrom || !previousPeriod?.validTo) {
			return '';
		}

		const from = this.dateUtil.setDateFormatByActiveLang(previousPeriod.validFrom, '', false, true);
		const to = this.dateUtil.setDateFormatByActiveLang(previousPeriod.validTo, '', false, true);

		return `${from} - ${to}`;
	}

	loadInvoicesByCostGroup() {
		// Hard reset bei „keine Kategorien“
		const categories = this.costGroup()?.invoiceCategories ?? [];
		if (categories.length === 0) {
			this.invoiceCategoryInvoiceDataSource = [];
			this.isLoading.set(false);
			return;
		}

		const area = this.sessionService.effectiveArea;
		const billingId = this.billingId();

		const requests = categories.map((category) =>
			this.invoiceService.readAllInvoice(area, billingId, category.exportId).pipe(
				map((list) => ({
					category,
					invoices: (list ?? []).sort((a, b) => {
						const aDate = (a as Invoice & { serviceDate?: string }).serviceDate;
						const bDate = (b as Invoice & { serviceDate?: string }).serviceDate;
						if (aDate > bDate) {
							return -1;
						}
						if (aDate < bDate) {
							return 1;
						}
						return 0;
					}),
				})),
				catchError(() => of({ category, invoices: [] }))
			)
		);

		(forkJoin(requests) as Observable<{ category: InvoiceCategory; invoices: Invoice[] }[]>)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.isLoading.set(false))
			)
			.subscribe((results) => {
				this.invoiceCategoryInvoicesMap.clear();
				for (const { category, invoices } of results) {
					this.invoiceCategoryInvoicesMap.set(category.exportId, new MatTableDataSource(invoices ?? []));
				}
				this.isExpanded = results.some((r) => r.invoices.length > 0);
				for (const ds of this.invoiceCategoryInvoicesMap.values()) {
					ds._updateChangeSubscription();
				}

				this.rebuildFlatTable();
			});
	}

	private toCurrency(val?: number): string {
		if (val == null) {
			return '';
		}
		const formatted = this.numberUtil.formatDecimal(val, 2, 2);

		// Symbol aus i18n beziehen; falls z. B. in EN „€“ trotzdem gewünscht ist, bleibt die Übersetzung einfach gleich.
		const symbol = this.t('currency.eur.symbol') || '€';
		return `${formatted}${symbol}`;
	}

	private t(key: string, params?: Record<string, unknown>): string {
		return this.transLocoService.translate(key, params);
	}

	newInvoice(category: InvoiceCategory): void {
		this.openInvoiceDialog({}, category);
	}

	private openInvoiceDialog(invoice: Invoice, invoiceCategory: InvoiceCategory): void {
		const dialogData: InvoiceDialogData = {
			selectedInvoice: invoice,
			invoiceCategory: invoiceCategory,
			billingId: this.billingId(),
			costGroup: this.costGroup(),
			periodId: this.period().exportId,
			costGroupType: this.costFormGroup.get('costType').value,
		};

		const dialogRef = this.dialog.open(InvoiceDialogComponent, {
			data: dialogData,
			autoFocus: false,
			restoreFocus: false,
			maxWidth: '95vw',
			width: 'auto',
		});

		dialogRef.afterClosed().subscribe((changed) => {
			if (changed) {
				// Reload cost group data after dialog closes
				const cg = this.costGroup();
				if (cg?.exportId) {
					this.emitCostGroupChange(cg.exportId);
				}
			}
		});
	}

	getCategoryInvoicesDataSource(flatDsGrouped: GroupedFlatRow[], category: InvoiceCategory): MatTableDataSource<Invoice> {
		return flatDsGrouped.find((row) => row.category.exportId === category.exportId)?.invoices ?? new MatTableDataSource([]);
	}

	/**
	 * Opens the dialog to create a new cost allocation.
	 * Once the dialog is closed, handles the result by refreshing the list of allocations
	 * and selects the newly created allocation automatically.
	 */
	addCostAllocation(currentCostGroup: CostGroup) {
		const matDialogRef = this.dialog.open(AllocationDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
			data: {
				costAllocationIdToEdit: undefined,
				costGroup: undefined,
				billingId: this.billingId(),
				billingPeriod: this.period(),
				costGroupCategory: this.costGroupType().category,
			},
		});
		matDialogRef.afterClosed().subscribe({
			next: async (result) => {
				if (result) {
					const enriched = await this.costAllocationDetailService.loadAllocationDetails(this.billingId(), this.period().exportId);
					this.availableAllocations.set(enriched);

					const newAllocation = enriched.find((a) => a.exportId === result.exportId);
					if (newAllocation) {
						this.selectedNewAllocation.set(newAllocation);
						this.saveAllocation(currentCostGroup);
					} else {
						// Fallback if the allocation isn't found in the refreshed list
						this.snackBar.warn(this.transLocoService.translate('warnings.allocationNotFoundAfterCreation'));
					}
				}
			},
		});
	}

	async editAllocation(currentCostGroup: CostGroup): Promise<void> {
		let enriched: CostAllocationDetail[] = [];
		try {
			enriched = await this.costAllocationDetailService.loadAllocationDetails(this.billingId(), this.period().exportId, this.costGroupType().category);
		} catch (e) {
			this.snackBar.error(this.transLocoService.translate('errors.loadingAllocations') + e.toString());
		}
		this.availableAllocations.set(enriched);

		// Reset selection und speichere original ID
		this.selectedNewAllocation.set(undefined);
		this.originalAllocationId.set(currentCostGroup.costAllocationExportId);
		this.allocationAutoSelected.set(false);

		if (currentCostGroup.costAllocationExportId) {
			// Wenn bereits eine Allocation zugewiesen ist, diese auswählen
			const usedCostAllocation = this.availableAllocations().filter((value) => value.exportId === currentCostGroup.costAllocationExportId);
			if (usedCostAllocation.length === 1) {
				this.selectedNewAllocation.set(usedCostAllocation[0]);
			}
		} else if (enriched.length === 1) {
			// Wenn nur eine Allocation verfügbar ist, diese automatisch auswählen (mit Animation)
			this.allocationAutoSelected.set(true);
			setTimeout(() => {
				this.selectedNewAllocation.set(enriched[0]);
			}, 100);
		}
		this.editableAllocationCostGroup.set(currentCostGroup);
	}

	saveAllocation(costGroup?: CostGroup) {
		if (costGroup) {
			if (this.selectedNewAllocation()?.exportId !== this.currentAllocation()?.exportId) {
				costGroup.costAllocationExportId = this.selectedNewAllocation()?.exportId;
				// Vollständige CostGroup übernehmen — sonst gehen invoiceCategories/-Deliveries/-Amounts beim Update verloren.
				const costGroupRequest: CostGroupRequest = { ...costGroup };
				this.costGroupService
					.updateCostGroup(this.sessionService.effectiveArea, this.billingId(), costGroup.exportId, costGroupRequest)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe({
						next: () => {
							this.emitCostGroupChange(costGroup?.exportId);
						},
						error: () => {
							this.snackBar.error(this.transLocoService.translate('errors.updatingBillingCostGroup'));
						},
					});
			}
		}
		this.editableAllocationCostGroup.set(undefined);
	}

	protected getAllocationName(allocation: CostAllocation) {
		return this.costAllocationDetailService.getAllocationDisplayName(allocation);
	}

	protected getCurrentDeliveryTranslations(currentCostGroup: CostGroup): [string, string][] {
		const currentDeliveryTranslations: Map<string, string> = new Map();
		const filteredRows = this.invoiceCategoryInvoiceDataSource.filter((row) => currentCostGroup.invoiceCategories.includes(row.category));

		for (const row of filteredRows) {
			this.processRowDeliveryValues(row, currentDeliveryTranslations);
		}
		return Array.from(currentDeliveryTranslations);
	}

	private processRowDeliveryValues(row: GroupedFlatRow, translations: Map<string, string>): void {
		for (const invoice of row.invoices.data) {
			if (invoice.invoiceDeliveryValues) {
				for (const deliveryValue of invoice.invoiceDeliveryValues) {
					translations.set(this.getTranslatedCommodity(deliveryValue), this.transLocoService.translate('enum.' + deliveryValue.unit));
				}
			}
		}
	}

	private static readonly SUM_SYMBOL_TYPES: InvoiceAmountType[] = [InvoiceAmountType.INVOICED_AMOUNT, InvoiceAmountType.CO2_COST];

	protected getInvoiceSums(costGroup: CostGroup, isCustomer: boolean, withSumSymbol: boolean = false): string[] {
		if (!costGroup?.invoiceAmounts?.length) {
			return [];
		}

		return costGroup.invoiceAmounts.map((invoiceAmount) => {
			const typeLabel = this.t('billing.invoiceAmountTypeChips.' + invoiceAmount.type);
			const sumSymbol = withSumSymbol && CostComponent.SUM_SYMBOL_TYPES.includes(invoiceAmount.type) ? '<b>Σ</b>' : '';

			let invoiceAmountDisplayLine: string;
			if (isCustomer) {
				invoiceAmountDisplayLine = this.getInvoicesAmountDisplayWithVatExpectation(
					invoiceAmount.customerSumGross,
					invoiceAmount.customerSumNet,
					invoiceAmount.customerSumVat
				);
			} else {
				invoiceAmountDisplayLine = this.getInvoicesAmountDisplayWithVatExpectation(invoiceAmount.sumGross, invoiceAmount.sumNet, invoiceAmount.sumVat);
			}

			return `${typeLabel}${sumSymbol}: ${invoiceAmountDisplayLine}`;
		});
	}

	protected getDeliveryValuesSum(costGroup: CostGroup): string[] {
		return this.getCurrentDeliveryTranslations(costGroup)
			.filter((delivery) => delivery[0]?.length)
			.map((delivery) => `${delivery[0]} (${delivery[1]})`);
	}

	protected getDeliveryValues(invoice: Invoice) {
		return (invoice.invoiceDeliveryValues ?? []).map((delivery) => {
			const commodity = this.getTranslatedCommodity(delivery);
			const amount = this.numberUtil.formatDecimal(delivery.amount, 0, 2);
			const unit = this.t('enum.' + delivery.unit);
			return `${commodity} (${amount} ${unit})`;
		});
	}
}
